import { createEffect, createSignal, on, Show, type JSX } from "solid-js";

import styles from "./deck.module.css";
import type { PageFrame } from "./types";

/** The figure a deck image sits in: a picture with its marks, or a library page. `failedText` is what shows in its
 *  place when it fails to load: each page knows why one might (the staff page refuses a picture rotated since the
 *  draft; a patient's session can end). `large` fills the slide with it, scaling a small image up in its own shape:
 *  the size on record, else the size it loads at. `overlay` draws over the image; `legend` and `caption` sit under. */
export function DeckImageFigure(props: {
  src: string;
  alt: string;
  width: number | null;
  height: number | null;
  failedText: string;
  large?: boolean;
  overlay?: JSX.Element;
  legend?: JSX.Element;
  caption: JSX.Element;
  /** Staff's words, under the caption; a large image shrinks to leave them room. */
  words?: string;
  /** Show only this part of the image, scaled to fill the figure in the part's own shape. Needs `large`. */
  frame?: PageFrame;
}): JSX.Element {
  // The size the image loaded at, for one whose size is not on record.
  const [loadedSize, setLoadedSize] = createSignal<{ width: number; height: number } | null>(null);
  const ratio = () => {
    if (!props.large) return null;
    const shape = (props.frame?.width ?? 1) / (props.frame?.height ?? 1);
    if (props.width && props.height) return (props.width / props.height) * shape;
    const size = loadedSize();
    return size ? (size.width / size.height) * shape : null;
  };
  // Framing needs the figure's shape, so an image whose size is not known yet shows whole until it loads.
  const framed = () => (ratio() === null ? undefined : props.frame);
  const [failed, setFailed] = createSignal(false);
  // A different image, or the same one at a new URL, gets a fresh try.
  createEffect(
    on(
      () => props.src,
      () => {
        setFailed(false);
        setLoadedSize(null);
      },
      { defer: true },
    ),
  );

  return (
    <figure
      class={styles["pic-fig"]}
      data-large={props.large ? "" : undefined}
      data-worded={props.words ? "" : undefined}
    >
      <Show
        when={!failed()}
        fallback={
          <div class={styles["pic-gone"]}>
            <b>{props.alt}</b>
            <span>{props.failedText}</span>
          </div>
        }
      >
        <div
          class={styles["pic"]}
          data-fill={ratio() === null ? undefined : ""}
          data-framed={framed() ? "" : undefined}
          style={{
            "--ratio": ratio() ?? undefined,
            "--fx": framed()?.x,
            "--fy": framed()?.y,
            "--fw": framed()?.width,
            "--fh": framed()?.height,
          }}
        >
          <img
            src={props.src}
            alt={props.alt}
            width={props.width ?? undefined}
            height={props.height ?? undefined}
            onLoad={(event) => {
              const { naturalWidth: width, naturalHeight: height } = event.currentTarget;
              if (width > 0 && height > 0) setLoadedSize({ width, height });
            }}
            onError={() => setFailed(true)}
          />
          {props.overlay}
        </div>
        {props.legend}
      </Show>
      <figcaption class={styles["pic-cap"]}>{props.caption}</figcaption>
      <Show when={props.words}>
        <p class={styles["pic-words"]}>{props.words}</p>
      </Show>
    </figure>
  );
}
